/**
 * LIFF Home Page
 * Only used for authentication - redirects to home after login
 * This page should not display any content, it's just for LIFF auth flow
 *
 * DEBUG MODE:
 * - Set DEBUG_MODE = true to show debug information on page
 * - Set DEBUG_MODE = false for normal silent redirect behavior
 */

import { useEffect, useState, useRef } from 'react';

// 🔧 DEBUG MODE: Set to false to disable debug messages
const DEBUG_MODE = false;
import { useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../../providers/LiffProvider';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface DebugInfo {
  step: string;
  liffInitialized: boolean;
  liffLoading: boolean;
  inClient: boolean;
  isLoggedIn: boolean;
  hasIdToken: boolean;
  idTokenPreview: string;
  isAuthenticated: boolean;
  isInitializing: boolean;
  apiCalled: boolean;
  apiSuccess: boolean;
  apiError: string;
  tokenSaved: boolean;
}

// No props needed - this page only handles authentication and redirect
const LiffHomePage: React.FC = () => {
  const config = useLiffConfig();
  const { isAuthenticated, isInitializing } = useAuth();

  // Debug state
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    step: '開始初始化',
    liffInitialized: false,
    liffLoading: false,
    inClient: false,
    isLoggedIn: false,
    hasIdToken: false,
    idTokenPreview: '',
    isAuthenticated: false,
    isInitializing: false,
    apiCalled: false,
    apiSuccess: false,
    apiError: '',
    tokenSaved: false,
  });

  const updateDebug = (updates: Partial<DebugInfo>) => {
    setDebugInfo(prev => ({ ...prev, ...updates }));
  };

  // Initialize LIFF SDK
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[LiffHomePage] LIFF initialized successfully');
      updateDebug({ step: 'LIFF 初始化成功', liffInitialized: true });
    },
    onInitError: (error) => {
      console.error('[LiffHomePage] LIFF initialization failed:', error);
      updateDebug({ step: `LIFF 初始化失敗: ${error.message}` });
    },
  });

  const [loading, setLoading] = useState(true);
  const [attemptedAutoLogin, setAttemptedAutoLogin] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Initialize LIFF when component mounts (only once)
    const initLiff = async () => {
      if (hasInitialized.current) {
        setLoading(false);
        return;
      }
      hasInitialized.current = true;

      try {
        updateDebug({ step: '正在初始化 LIFF...' });
        if (!liff.isInitialized && !liff.isLoading) {
          await liff.init();
        }
      } catch (error) {
        console.error('[LiffHomePage] LIFF init error:', error);
        updateDebug({ step: `LIFF 初始化錯誤: ${error instanceof Error ? error.message : '未知錯誤'}` });
      }
      setLoading(false);
    };

    initLiff();
  }, []);

  // Auto-login with LIFF ID Token when in LINE App
  useEffect(() => {
    const autoLogin = async () => {
      try {
        // If already authenticated or AuthContext is initializing, redirect to home
        if (isAuthenticated || isInitializing) {
          console.log('[LiffHomePage] User authenticated or initializing, redirecting to home');
          updateDebug({ step: '已認證或正在初始化，將跳轉到首頁' });
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
          return;
        }

        updateDebug({ step: '檢查 LIFF 狀態...', isAuthenticated, isInitializing });

        if (!liff.isInitialized) {
          updateDebug({ step: 'LIFF 未初始化，正在初始化...' });
          await liff.init();
        }

        const inClient = liff.isInClient();
        const loggedIn = liff.isLoggedIn();

        updateDebug({
          step: '檢查 LIFF 登入狀態',
          inClient,
          isLoggedIn: loggedIn,
        });

        // Check if in LINE App and logged in to LIFF
        if (inClient && loggedIn) {
          updateDebug({ step: '在 LINE App 中且已登入 LIFF' });

          const idToken = liff.getIDToken();
          if (idToken) {
            const preview = idToken.substring(0, 20) + '...';
            updateDebug({
              step: '獲取到 ID Token，正在調用 API...',
              hasIdToken: true,
              idTokenPreview: preview,
            });

            const response = await api.post<any>('/api/auth/line/liff', { idToken });

            updateDebug({
              step: 'API 調用完成',
              apiCalled: true,
              apiSuccess: response.success,
              apiError: response.error?.message || '',
            });

            if (response.success && response.data) {
              const { token } = response.data;
              localStorage.setItem('auth_token', token);
              updateDebug({
                step: '登入成功！將跳轉到首頁...',
                tokenSaved: true,
              });
              setTimeout(() => {
                window.location.href = '/';
              }, 2000);
              return;
            } else {
              updateDebug({
                step: `API 失敗: ${response.error?.message || '未知錯誤'}`,
              });
            }
          } else {
            updateDebug({ step: '無法獲取 ID Token' });
          }
        } else {
          updateDebug({
            step: `不在 LINE App 或未登入 LIFF (inClient: ${inClient}, loggedIn: ${loggedIn})`,
          });
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : '未知錯誤';
        console.error('[LiffHomePage] Auto login failed:', error);
        updateDebug({
          step: `自動登入失敗: ${errorMsg}`,
        });
      }
      setAttemptedAutoLogin(true);
    };

    if (!loading && !attemptedAutoLogin && !liff.isLoading) {
      autoLogin();
    }
  }, [loading, isAuthenticated, isInitializing, attemptedAutoLogin, liff.isLoading, liff.isInitialized, liff.isInClient, liff.isLoggedIn, liff.getIDToken]);

  // Show loading while processing
  if (loading || liff.isLoading || !attemptedAutoLogin) {
    return (
      <div className="liff-loading">
        <div className="liff-loading__spinner"></div>
        <p className="liff-loading__text">載入中...</p>
      </div>
    );
  }

  // If not in debug mode, return null (silent redirect)
  if (!DEBUG_MODE) {
    return null;
  }

  // Show debug information (only when DEBUG_MODE is true)
  return (
    <div style={{
      padding: '20px',
      fontFamily: 'monospace',
      fontSize: '14px',
      lineHeight: '1.6',
      maxWidth: '600px',
      margin: '0 auto',
    }}>
      <h2 style={{ color: '#06C755', marginBottom: '20px' }}>LIFF 登入除錯訊息</h2>

      <div style={{
        background: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '15px',
      }}>
        <strong>當前步驟:</strong> {debugInfo.step}
      </div>

      <div style={{
        background: '#fff',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #ddd',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>狀態詳情:</h3>
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          <li>LIFF 已初始化: <strong>{debugInfo.liffInitialized ? '✓' : '✗'}</strong></li>
          <li>LIFF 載入中: <strong>{debugInfo.liffLoading ? '✓' : '✗'}</strong></li>
          <li>在 LINE App 中: <strong>{debugInfo.inClient ? '✓' : '✗'}</strong></li>
          <li>已登入 LIFF: <strong>{debugInfo.isLoggedIn ? '✓' : '✗'}</strong></li>
          <li>有 ID Token: <strong>{debugInfo.hasIdToken ? '✓' : '✗'}</strong></li>
          {debugInfo.idTokenPreview && <li>ID Token: <code style={{ fontSize: '12px' }}>{debugInfo.idTokenPreview}</code></li>}
          <li>已認證: <strong>{debugInfo.isAuthenticated ? '✓' : '✗'}</strong></li>
          <li>正在初始化: <strong>{debugInfo.isInitializing ? '✓' : '✗'}</strong></li>
          <li>API 已調用: <strong>{debugInfo.apiCalled ? '✓' : '✗'}</strong></li>
          <li>API 成功: <strong>{debugInfo.apiSuccess ? '✓' : '✗'}</strong></li>
          {debugInfo.apiError && <li>API 錯誤: <span style={{ color: 'red' }}>{debugInfo.apiError}</span></li>}
          <li>Token 已保存: <strong>{debugInfo.tokenSaved ? '✓' : '✗'}</strong></li>
        </ul>
      </div>

      <div style={{
        marginTop: '15px',
        fontSize: '12px',
        color: '#666',
      }}>
        <p>LIFF ID: {config.liffId || '未設置'}</p>
        <p>App ID: {config.appId || '未設置'}</p>
        <p>Service URL: {config.serviceUrl || '未設置'}</p>
      </div>
    </div>
  );
};

export default LiffHomePage;
